import { ChangeDetectionStrategy, Component, booleanAttribute, computed, inject, input } from '@angular/core';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdCheatSheetScopeKey, HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdCircuitGateCardComponent, hdCircuitGateText } from './hd-circuit-gate-card.component';

interface CircuitGateLineVm {
  readonly line: number;
  readonly keynote: string;
  readonly text: string;
}

/** Lijnvolgorde zoals in een hexagram: 6 bovenaan, 1 onderaan. */
const HD_CIRCUIT_GATE_LINE_ORDER: readonly number[] = [6, 5, 4, 3, 2, 1];

interface CircuitGateSectionVm {
  /** Optionele ondertitel uit de scope (`gates.<poort>.subtitle`, bv. bij liefde). */
  readonly subtitle: string;
  readonly description: string;
  /** Bestaande korte I Ching-omschrijving van de poort (gates-scope). */
  readonly ichingDescription: string;
  /** De twee algemene poort-keynotes (gates-scope), samengevoegd. */
  readonly keynotes: string;
  /** Kop boven de lijnen (scope-sleutel `linesTitle`). */
  readonly linesTitle: string;
  /** De zes lijnen van de poort in deze scope (`gates.<poort>.lines.<1-6>`), enkel met `showLines`. */
  readonly lines: readonly CircuitGateLineVm[];
}

/**
 * Eén volledige poortsectie van een circuit-/thema-paneel (geheugen, liefde,
 * mystiek): het blokje ({@link HdCircuitGateCardComponent}) als titel, met
 * daaronder, licht ingesprongen, de optionele ondertitel en de omschrijving uit
 * de opgegeven cheat-sheet-scope en tot slot de bestaande I Ching-omschrijving
 * (en op vraag de twee poort-keynotes) uit de `human-design/gates`-scope.
 * Met `showLines` volgen de zes lijnen van de poort in die scope (keynote +
 * korte duiding, 6 bovenaan zoals in een hexagram), bv. de mystieke lijnen.
 *
 * Het host-attribuut `data-section-gate` is het scroll-doel van de panelen
 * (HdPanelSectionScroller); alle content leest het component zelf.
 */
@Component({
  selector: 'hd-circuit-gate-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdCircuitGateCardComponent],
  host: { '[attr.data-section-gate]': 'gate()' },
  template: `
    <hd-circuit-gate-card [scope]="scope()" [gate]="gate()" [rail]="rail()" [size]="cardSize()" />

    <div class="hd-circuit-gate-section__content">
      @if (vm().subtitle) {
        <p class="hd-circuit-gate-section__subtitle">{{ vm().subtitle }}</p>
      }

      @if (vm().description) {
        <p class="hd-circuit-gate-section__description">{{ vm().description }}</p>
      }

      @if (vm().ichingDescription) {
        <p class="hd-circuit-gate-section__iching">{{ vm().ichingDescription }}</p>
      }

      @if (vm().keynotes) {
        <p class="hd-circuit-gate-section__keynotes">{{ vm().keynotes }}</p>
      }

      @if (vm().lines.length > 0) {
        <div class="hd-circuit-gate-section__lines">
          @if (vm().linesTitle) {
            <p class="hd-circuit-gate-section__lines-title">{{ vm().linesTitle }}</p>
          }
          <ol class="hd-circuit-gate-section__line-list">
            @for (line of vm().lines; track line.line) {
              <li class="hd-circuit-gate-section__line" [attr.data-line]="line.line">
                <span class="hd-circuit-gate-section__line-number">{{ gate() }}.{{ line.line }}</span>
                <span class="hd-circuit-gate-section__line-body">
                  <span class="hd-circuit-gate-section__line-keynote">{{ line.keynote }}</span>
                  @if (line.text) {
                    <span class="hd-circuit-gate-section__line-text">{{ line.text }}</span>
                  }
                </span>
              </li>
            }
          </ol>
        </div>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
      color: #3d414e;
    }

    /* Ingesprongen onder het blokje (zoals de voice-secties): de tekst leest
       als detail bij de titel, niet als een gelijkwaardig niveau. */
    .hd-circuit-gate-section__content {
      padding: 7px 2px 0 16px;
    }

    .hd-circuit-gate-section__subtitle {
      margin: 2px 0 0;
      font-size: 0.92em;
      font-weight: 700;
      line-height: 1.3;
    }

    .hd-circuit-gate-section__description {
      margin: 4px 0 0;
      font-size: 1em;
      line-height: 1.5;
      white-space: pre-line;
    }

    /* I Ching-omschrijving + keynotes als gedempte slotregels, zoals op de
       cheat sheets (de bestaande poort-info onder de thema-tekst). */
    .hd-circuit-gate-section__iching {
      margin: 8px 0 0;
      font-size: 0.9em;
      font-style: italic;
      color: rgba(61, 65, 78, 0.65);
    }

    .hd-circuit-gate-section__keynotes {
      margin: 3px 0 0;
      font-size: 0.9em;
      font-weight: 700;
      color: rgba(61, 65, 78, 0.75);
    }

    /* Lijnen: compacte lijst met het lijnnummer als vaste kolom. */
    .hd-circuit-gate-section__lines {
      margin-top: 10px;
    }

    .hd-circuit-gate-section__lines-title {
      margin: 0 0 4px;
      font-size: 0.85em;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: rgba(61, 65, 78, 0.7);
    }

    .hd-circuit-gate-section__line-list {
      margin: 0;
      padding: 0;
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    .hd-circuit-gate-section__line {
      display: flex;
      align-items: baseline;
      gap: 8px;
      font-size: 0.92em;
      line-height: 1.4;
    }

    .hd-circuit-gate-section__line-number {
      flex: 0 0 auto;
      min-width: 2.6em;
      font-weight: 800;
      font-variant-numeric: tabular-nums;
      color: rgba(61, 65, 78, 0.6);
    }

    .hd-circuit-gate-section__line-body {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    .hd-circuit-gate-section__line-keynote {
      font-weight: 700;
    }

    .hd-circuit-gate-section__line-text {
      color: rgba(61, 65, 78, 0.85);
    }
  `,
})
export class HdCircuitGateSectionComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly scope = input.required<HdCheatSheetScopeKey>();
  readonly gate = input.required<number>();
  readonly rail = input<string | undefined>(undefined);
  /** Doorgegeven aan het blokje; `compact` voor smalle gedockte panelen. */
  readonly cardSize = input<'default' | 'compact'>('default');
  /** Toon de twee algemene poort-keynotes als slotregel. */
  readonly showKeynotes = input(false, { transform: booleanAttribute });
  /** Toon de zes lijnen van de poort uit de scope (`gates.<poort>.lines.<1-6>`). */
  readonly showLines = input(false, { transform: booleanAttribute });

  protected readonly vm = computed<CircuitGateSectionVm>(() => {
    const gate = this.gate();
    const scope = this.scope();
    const lang = this.contentLang.effectiveLang();
    const translations = this.gateInfo.gateInfoIn(gate, lang).translations;
    const text = (key: string) => hdCircuitGateText(this.gateInfo, lang, scope, `gates.${gate}.${key}`);
    return {
      subtitle: text('subtitle'),
      description: text('description'),
      ichingDescription: translations.shortIchingDescription,
      keynotes: this.showKeynotes()
        ? [translations.keynote1, translations.keynote2].filter(Boolean).join(' · ')
        : '',
      linesTitle: this.showLines() ? hdCircuitGateText(this.gateInfo, lang, scope, 'linesTitle') : '',
      lines: this.showLines()
        ? HD_CIRCUIT_GATE_LINE_ORDER
          .map(line => ({ line, keynote: text(`lines.${line}.keynote`), text: text(`lines.${line}.text`) }))
          .filter(line => line.keynote !== '')
        : [],
    };
  });

  constructor() {
    // Beide content-talen eager laden zodat taalwissel én NL-fallback meteen
    // correcte tekst opleveren, ook bij geïsoleerd (standalone) gebruik.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }
}
